<template>
    <div class="flex flex-col xxs:w-full md:w-[calc(50%-10px)] lg:w-[calc(33%-10px)] xxs:h-auto md:min-h-[440px] news-card mt-4 bg-white border border-interactive-9 rounded hover:drop-shadow-[0_2px_16px_rgba(100,100,100,0.21)] hover:z-10 hover:opacity-100 hover:border-white">
        <router-link
            :to="`/news/${article.uid}`"
            v-if="hasThumbnail"
            class="block w-full h-auto"
        >
            <Image
                :src="thumbnail"
                type="thumbnail"
                :lazyLoading="index >= 3"
                :index="index"
            />
        </router-link>
        <div class="w-full flex flex-col px-4 pt-4 pb-6 box-border flex-1">
            <div v-if="isScheduled && !isPastDate" class="w-full flex">
                <img src="@/assets/icons/schedule.svg" alt="Schedule" class="w-4 mr-2" />
                <span class="text-body-7 text-default-4">Scheduled on {{formattedDate}}</span>
            </div>
            <div v-else class="w-full text-body-7 text-default-4">
                {{formattedDate}}
            </div>

            <router-link :to="`/news/${article.uid}`">
                <h1 class="text-body-5 text-default-1 w-full overflow-hidden overflow-ellipsis max-h-[66px] multi-line-ellipsis clamp-3-lines" :class="{hasThumbnail: 'mt-4'}">
                    {{article.title}}
                </h1>
            </router-link>

            <div v-html="processedHtml" class="mt-2 text-body-6 text-default-2 w-full overflow-hidden overflow-ellipsis max-h-[60px] multi-line-ellipsis clamp-3-lines"/>
            <div class="flex flex-1"></div>
            <div class="w-full flex mt-4" v-if="hasTags">
                <NewsTag :tags="article.tags"/>
            </div>

            <div class="flex w-full justify-between items-center mt-4">
                <Like v-if="!isTrashed && !isScheduled"
                      :is-liked="article.is_liked" :article-uid="article.uid" :likes-count="article.total_likes" @toggle-like="toggleLike" />
                <div class="relative ml-auto">
                    <ActionMenuNewsCard
                        @view="emit('view')"
                        :is-scheduled="isScheduled"
                        :is-trashed="isTrashed"
                        :is-on-current-organization="article.organization === organization.slug"
                        @share="emit('share', article)"
                        @edit="emit('edit')"
                        @delete="handleDelete"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import {computed} from "vue";
import ActionMenuNewsCard from "@/components/custom/news/ActionMenuNewsCard.vue";
import Like from "@/components/custom/news/Like.vue";
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import {toast} from "vue3-toastify";
import {useNews} from "@/composables/useNews";
import NewsTag from "@/components/custom/news/NewsTag.vue";
import Image from "@/components/custom/Image.vue";
import {useFormatDate} from "@/composables/useFormatDate";

const {organization} = storeToRefs(useAuth());

const {deleteArticle} = useNews();

const props = defineProps({
    article: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    }
})

const emit = defineEmits(['toggleLike', 'share', 'edit', 'view', 'articleDeleted'])

function toggleLike() {
    emit('toggleLike' );
}

const isScheduled = computed(() => {
    if(!props.article) {
        return false;
    }

    return props.article.published_at !== null
})

const isTrashed = computed(() => {
    if(!props.article) {
        return false;
    }

    return props.article.deleted_at !== null;
})

const hasThumbnail = computed(() => {
    if(!props.article) {
        return false;
    }

    return props.article.thumbnail && props.article.thumbnail.length > 0;
})

const thumbnail = computed(() => {
    if(!props.article || props.article.thumbnail.length === 0) {
        return '';
    }

    return props.article.thumbnail;
})

const isPastDate = computed(() => {
    const dateToCheck = new Date(props.article.published_at);

    const currentDate = new Date();

    return dateToCheck < currentDate;
});

const formattedDate = computed(() => {
    if(!props.article) {
        return '';
    }

    let date = props.article.created_at;
    if(isScheduled.value) {
        date = props.article.published_at;
    }

    return useFormatDate(date);
});

const processedHtml = computed(() => {
    return props.article.body.replace(/<img[^>]*>/g, '');
});

const hasTags = computed(() => {
    return props.article.tags.length > 0;
})

function handleDelete() {
    deleteArticle(organization.value.slug, props.article.uid)
        .then(() => {
            toast.success("The news was deleted successfully.");
            emit('articleDeleted');
        })
        .catch((error) => {
            console.log(error);
            toast.error("There was an error trying to delete the news.");
        })
}

</script>
